/**
 * @file LegalAddMemberField.tsx
 * @description «+ Добавить документ в группу» — поиск по названию
 * (phase19.16, SB141; uiux-legal-docs §4).
 *
 * В списке только документы «Текст» и «PDF» (группа в группу не входит) и
 * только те, что ещё не в этой группе. Документ из другой группы виден с
 * пометкой «в группе «…» — перенесём сюда»: документ бывает только в одной
 * группе, перенос подтверждает родитель. Список документов читается при
 * первом открытии (и после каждого добавления — родитель меняет `refreshKey`).
 */

import { useEffect, useMemo, useState } from "react";

import { Combobox, type ComboboxOption } from "../ui/combobox";
import { listLegalDocs, type LegalDocumentListItemDto } from "../../lib/api-legal";
import { LEGAL_KIND_META } from "../../lib/legal/legal-doc-view";

interface Props {
  groupId: string;
  /** Документы, которые уже в группе. */
  memberIds: readonly string[];
  /** Выбран документ (у документа из другой группы `group` заполнен — перенос подтверждает родитель). */
  onAdd: (doc: LegalDocumentListItemDto) => void;
  refreshKey: number;
  disabled?: boolean;
}

/** Пояснение строки выбора: вид, публикация, чужая группа. */
function optionDescription(doc: LegalDocumentListItemDto): string {
  const parts = [LEGAL_KIND_META[doc.kind].label, doc.visibleOnSite ? "опубликован" : "не опубликован"];
  if (doc.group) parts.push(`в группе «${doc.group.title}» — перенесём сюда`);
  return parts.join(" · ");
}

export function LegalAddMemberField({ groupId, memberIds, onAdd, refreshKey, disabled }: Props) {
  const [docs, setDocs] = useState<LegalDocumentListItemDto[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    listLegalDocs()
      .then(({ items }) => !cancelled && setDocs(items))
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const options = useMemo<ComboboxOption[]>(() => {
    const inGroup = new Set(memberIds);
    return (docs ?? [])
      .filter((d) => d.kind !== "GROUP" && d.id !== groupId && !inGroup.has(d.id))
      .map((d) => ({ value: d.id, label: d.title, description: optionDescription(d) }));
  }, [docs, memberIds, groupId]);

  return (
    <div className="flex flex-col gap-1">
      <Combobox
        ariaLabel="Добавить документ в группу"
        options={options}
        value={null}
        onChange={(id) => {
          const doc = docs?.find((d) => d.id === id);
          if (doc) onAdd(doc);
        }}
        placeholder="+ Добавить документ"
        searchPlaceholder="Название документа"
        emptyText={docs === null ? "Загружаем документы…" : "Подходящих документов нет — группы в группу не добавляются"}
        disabled={disabled || failed}
      />
      {failed ? <p className="type-caption text-destructive">Не удалось загрузить документы — обновите страницу.</p> : null}
    </div>
  );
}
